import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Tabs from '@cloudscape-design/components/tabs';
import { useSearchParams } from 'react-router-dom';
import { FiltersSettings } from '../components/settings/FiltersSettings';
import { AISummarizationSettings } from '../components/settings/AISummarizationSettings';
import { DataSyncSettings } from '../components/settings/DataSyncSettings';
import { ScopedOperations } from '../components/settings/ScopedOperations';
import { ExperimentsPanel } from '../components/experiments/ExperimentsPanel';

/**
 * Settings, split into tabs.
 *
 * Previously four stacked containers, which buried the targeted operations
 * ("run partial sync") below a long scroll of configuration forms — the two
 * things an operator does here are edit config and run a job, and the second was
 * effectively hidden.
 *
 * The split follows that division: what the system is configured to do, versus
 * what to run right now. Sync and targeted operations share a tab deliberately,
 * so the queue depth and DLQ indicators sit on the same screen as the run
 * button; they are the only live signal that work is in flight, and previously
 * they lived in a different container from the control that starts it.
 *
 * The active tab is kept in the URL so a tab is linkable and survives a reload.
 */
const TABS = [
  {
    id: 'configuration',
    label: 'Configuration',
    content: (
      <SpaceBetween size="l">
        <FiltersSettings />
        <AISummarizationSettings />
      </SpaceBetween>
    ),
  },
  {
    id: 'experiments',
    label: 'AI experiments',
    // Evaluating a candidate model or prompt is a configuration activity, so it
    // belongs beside the config it is testing rather than in the main nav. Run
    // details stay a standalone route at /experiments/:runId so a run remains
    // deep-linkable and shareable.
    content: <ExperimentsPanel />,
  },
  {
    id: 'operations',
    label: 'Sync & operations',
    content: (
      <SpaceBetween size="l">
        <DataSyncSettings />
        <ScopedOperations />
      </SpaceBetween>
    ),
  },
];

export function SettingsPage() {
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab');
  const activeTabId = TABS.some((t) => t.id === requested) ? (requested as string) : TABS[0].id;

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        description="Configure what is fetched and how it is summarized, and run targeted operations."
      >
        Settings
      </Header>
      <Tabs
        tabs={TABS}
        activeTabId={activeTabId}
        onChange={({ detail }) => {
          const next = new URLSearchParams(params);
          next.set('tab', detail.activeTabId);
          setParams(next, { replace: true });
        }}
      />
    </SpaceBetween>
  );
}
